import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import type { EmailOtpType, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { getBasename } from '@/utils/url';
import { hasProfileQueryKey } from '@/features/profile/hooks/useHasProfile';
import { isProfileComplete } from '@/features/profile/isProfileComplete';
import Dashboard from './Dashboard';

/** How long to wait for the PKCE code exchange to produce a session. */
const SESSION_WAIT_TIMEOUT_MS = 5000;

export default function AuthCallback() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [sessionChecked, setSessionChecked] = useState(false);
  const routerBasename = getBasename();

  useEffect(() => {
    let cancelled = false;
    let waitTimer: ReturnType<typeof setTimeout> | undefined;
    let authSubscription: { unsubscribe: () => void } | undefined;

    // Social sign-in (Google) authenticates without a usable profile — route such
    // users to /complete-profile instead of the app home. "Usable" is decided by
    // isProfileComplete, the same rule the route guards apply: a row whose
    // username is missing does not count, or the user would land in the app with
    // no identity and no way back.
    const routeBySession = async (session: Session) => {
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('id, username')
        .eq('id', session.user.id)
        .maybeSingle();

      const returnTo = sessionStorage.getItem('auth_return_to');
      sessionStorage.removeItem('auth_return_to');

      if (profileError) {
        // Couldn't determine — don't treat it as "no profile". Go home and
        // let ProtectedRoute's own check (with retry) resolve the routing.
        navigate(returnTo || '/home', { replace: true });
        return;
      }

      const complete = isProfileComplete({
        hasRow: !!profile,
        username: profile?.username ?? null,
        provider: session.user.app_metadata?.provider,
      });

      // Seed the guard cache so the destination route doesn't re-run the
      // identical query (avoids a second round-trip + loading flash). Seeding the
      // same value the guard would compute is what keeps the two from disagreeing.
      queryClient.setQueryData(hasProfileQueryKey(session.user.id), complete);
      navigate(complete ? (returnTo || '/home') : '/complete-profile', { replace: true });
    };

    // PKCE is the supabase-js v2 default: Google comes back with `?code=`, which
    // the client exchanges on its own (detectSessionInUrl) and strips from the
    // URL. By the time this effect runs there is nothing left to parse — so wait
    // for the session instead of reading the URL.
    const waitForSession = () =>
      new Promise<Session | null>((resolve) => {
        supabase.auth.getSession().then(({ data: { session } }) => {
          if (session) {
            resolve(session);
            return;
          }

          const { data } = supabase.auth.onAuthStateChange((_event, nextSession) => {
            if (nextSession) resolve(nextSession);
          });
          authSubscription = data.subscription;
          waitTimer = setTimeout(() => resolve(null), SESSION_WAIT_TIMEOUT_MS);
        });
      });

    const handleAuthCallback = async () => {
      try {
        // Verificar se é fluxo de confirmação de email (query params)
        const urlParams = new URLSearchParams(window.location.search);
        const token = urlParams.get('token');
        const type = urlParams.get('type');
        const next = urlParams.get('next') || '/home';

        if (token && type) {
          // Fluxo de confirmação de email
          const { data, error } = await supabase.auth.verifyOtp({
            token_hash: token,
            type: type as EmailOtpType,
          });

          if (error) {
            console.error('Erro na verificação:', error);
            navigate('/auth/error');
            return;
          }

          if (data.user) {
            const returnTo = sessionStorage.getItem('auth_return_to');
            sessionStorage.removeItem('auth_return_to');
            navigate(returnTo || next, { replace: true });
          } else {
            navigate('/auth/error');
          }
          setSessionChecked(true);
          return;
        }

        // Fluxo PKCE (hash params) - código original
        const hash = window.location.hash.substring(1);
        const hashParams = new URLSearchParams(hash);
        const access_token = hashParams.get('access_token');
        const refresh_token = hashParams.get('refresh_token');
        const hashType = hashParams.get('type');

        // Se for um logout, apenas redireciona para home
        if (hashType === 'signout') {
          navigate(routerBasename, { replace: true });
          return;
        }

        // Sem tokens no hash: fluxo PKCE, em que o próprio client já trocou o
        // code. Espera a sessão em vez de desistir para '/'.
        if (!access_token || !refresh_token) {
          const session = await waitForSession();
          if (cancelled) return;

          if (session) {
            await routeBySession(session);
          } else {
            navigate('/', { replace: true });
          }
          setSessionChecked(true);
          return;
        }

        // Se for um login e tiver tokens, configura a sessão
        supabase.auth.setSession({
          access_token,
          refresh_token,
        }).then(async ({ data: { session } }) => {
          if (session) {
            await routeBySession(session);
          } else {
            navigate('/', { replace: true });
          }
          setSessionChecked(true);
        }).catch(() => {
          navigate('/', { replace: true });
        });
      } catch (error) {
        console.error('Erro no callback:', error);
        navigate('/auth/error');
      }
    };

    handleAuthCallback();

    return () => {
      cancelled = true;
      if (waitTimer) clearTimeout(waitTimer);
      authSubscription?.unsubscribe();
    };
  }, [navigate]);

  // Se ainda não verificou a sessão, mostra loading
  if (!sessionChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#160C0C] text-white text-lg">
        Processing authentication...
      </div>
    );
  }

  return null;
} 